import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CompanyResponse,
  DedupeScope,
  ListDuplicatesResponse,
  ListResponse,
  ProfileResponse,
} from '@shared/schemas';

import {
  emptyDedupe,
  emptyDuplicate,
  emptyMutation,
  initialState,
  type MutationKind,
  type MutationResult,
} from './list-detail.state';

/** Payload for `loadSucceeded`: everything the initial multi-resource load returns. */
export interface LoadedDetail {
  list: ListResponse;
  profiles: ProfileResponse[];
  companies: CompanyResponse[];
  commentCount: number;
  removedProfiles: ProfileResponse[];
}

/**
 * Admin List Detail slice. Reducers are pure state transitions only; every side
 * effect (the initial load, the mutations, the mutation→reload) lives in the
 * saga, which dispatches these `load*` / `mutation*` transitions as it progresses.
 */
const slice = createSlice({
  name: 'listDetail',
  initialState,
  reducers: {
    // ── Load lifecycle ──
    // `loadStarted` keeps the existing `list` so a post-mutation reload does not
    // blank the page (the page guards on `list`, not on `loadStatus`).
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<LoadedDetail>) => {
      const { list, profiles, companies, commentCount, removedProfiles } = action.payload;
      state.list = list;
      state.profiles = profiles;
      state.companies = companies;
      state.commentCount = commentCount;
      state.removedProfiles = removedProfiles;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Mutation lifecycle (enrich / deactivate / restore / delete) ──
    mutationStarted: (state, action: PayloadAction<MutationKind>) => {
      state.mutation = { status: 'pending', kind: action.payload, result: null, error: null };
    },
    mutationSucceeded: (
      state,
      action: PayloadAction<{ kind: MutationKind; result?: MutationResult | null }>,
    ) => {
      state.mutation = {
        status: 'succeeded',
        kind: action.payload.kind,
        result: action.payload.result ?? null,
        error: null,
      };
      if (action.payload.kind === 'delete') state.deleted = true;
      // The server moves a draft list to in_progress when its enrichment is
      // launched (#748); mirrored here, the page does not reload after enrich.
      if (action.payload.kind === 'enrich' && state.list?.status === 'draft') {
        state.list.status = 'in_progress';
      }
    },
    mutationFailed: (
      state,
      action: PayloadAction<{ kind: MutationKind; message: string }>,
    ) => {
      state.mutation = {
        status: 'failed',
        kind: action.payload.kind,
        result: null,
        error: action.payload.message,
      };
    },
    /** Acknowledge a settled mutation (page calls after handling success/failure). */
    mutationCleared: (state) => {
      state.mutation = emptyMutation;
    },

    // ── Deduplicate preview (#640) ──
    dedupePreviewStarted: (state, action: PayloadAction<DedupeScope>) => {
      state.dedupe = { status: 'loading', scope: action.payload, preview: null, error: null };
    },
    dedupePreviewSucceeded: (state, action: PayloadAction<ListDuplicatesResponse>) => {
      state.dedupe.status = 'ready';
      state.dedupe.preview = action.payload;
    },
    dedupePreviewFailed: (state, action: PayloadAction<string>) => {
      state.dedupe.status = 'failed';
      state.dedupe.error = action.payload;
    },
    /** Close the preview (dialog dismissed, or the removal settled). */
    dedupeCleared: (state) => {
      state.dedupe = emptyDedupe;
    },

    // ── Duplicate (#662) ──
    duplicateNamesStarted: (state) => {
      state.duplicate = { status: 'loading', takenNames: [] };
    },
    /** The names are in (or the read failed and only the list's own is known): open. */
    duplicateDialogOpened: (state, action: PayloadAction<string[]>) => {
      state.duplicate = { status: 'open', takenNames: action.payload };
    },
    /** Close the dialog (dismissed, or the copy created). */
    duplicateCleared: (state) => {
      state.duplicate = emptyDuplicate;
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const listDetailActions = slice.actions;
export const listDetailReducer = slice.reducer;
